import { useState, useCallback, useEffect, useMemo } from 'react';
import { getDatesForRangeType } from '@components/Kendo/DateRangePickerReducer';
import { THIS_MONTH, LAST_MONTH, THIS_QUARTER, LAST_QUARTER, THIS_YEAR, LAST_YEAR, CUSTOM_RANGE } from '../../../../constants/Kendo/rangeOptions';
import { validateDateRange, strictDateValidation } from '../../../../utils/dateValidation';
import { loadCacheFromLocalStorage, saveCacheToLocalStorage } from '../../../../helpers/localStorage';
const CACHE_KEY = 'kendo/ia_insights_v2';
const initialDateRange = getDatesForRangeType(THIS_MONTH, '', '');
const defaultFilters = {
    values: {
        dateRange: {
            rangeType: THIS_MONTH,
            startDate: initialDateRange[0],
            endDate: initialDateRange[1]
        }
    },
    active: {
        dateRange: true
    }
};
function loadFilters() {
    var _a, _b, _c, _d;
    const cached = loadCacheFromLocalStorage(CACHE_KEY);
    if (((_b = (_a = cached === null || cached === void 0 ? void 0 : cached.values) === null || _a === void 0 ? void 0 : _a.dateRange) === null || _b === void 0 ? void 0 : _b.startDate) && ((_d = (_c = cached === null || cached === void 0 ? void 0 : cached.values) === null || _c === void 0 ? void 0 : _c.dateRange) === null || _d === void 0 ? void 0 : _d.endDate)) {
        return cached;
    }
    return defaultFilters;
}
export default function useFilters() {
    const [filters, setFilters] = useState(loadFilters);
    useEffect(() => {
        saveCacheToLocalStorage(CACHE_KEY, filters);
    }, [filters]);
    const onChangeDateFilter = useCallback((fieldName) => {
        return (dateFieldName, value) => {
            setFilters((prevFilters) => {
                const currentValues = prevFilters.values[fieldName];
                if (dateFieldName === 'rangeType') {
                    const newDates = getDatesForRangeType(value, '', '');
                    const validation = validateDateRange(newDates[0], newDates[1]);
                    if (validation.isValid) {
                        return Object.assign(Object.assign({}, prevFilters), { values: Object.assign(Object.assign({}, prevFilters.values), { [fieldName]: Object.assign(Object.assign({}, currentValues), { rangeType: value, startDate: newDates[0], endDate: newDates[1] }) }) });
                    }
                    return prevFilters;
                }
                const currentDateValues = currentValues;
                const strictValidation = strictDateValidation(value);
                if (!strictValidation.isValid) {
                    return prevFilters;
                }
                const startDate = dateFieldName === 'startDate' ? value : currentDateValues.startDate || '';
                const endDate = dateFieldName === 'endDate' ? value : currentDateValues.endDate || '';
                const validation = validateDateRange(startDate, endDate);
                if (validation.isValid) {
                    const newValues = Object.assign(Object.assign({}, currentValues), { rangeType: CUSTOM_RANGE, [dateFieldName]: value });
                    return Object.assign(Object.assign({}, prevFilters), { values: Object.assign(Object.assign({}, prevFilters.values), { [fieldName]: newValues }) });
                }
                return prevFilters;
            });
        };
    }, []);
    const onToggleFilter = useCallback((fieldName) => {
        return () => {
            setFilters((prevFilters) => (Object.assign(Object.assign({}, prevFilters), { active: Object.assign(Object.assign({}, prevFilters.active), { [fieldName]: !prevFilters.active[fieldName] }) })));
        };
    }, []);
    const onResetFilters = useCallback(() => {
        setFilters(defaultFilters);
    }, []);
    const onResetCache = useCallback(() => {
        localStorage.removeItem(CACHE_KEY);
        setFilters(defaultFilters);
    }, []);
    const filtersConfig = useMemo(() => [
        {
            field: 'dateRange',
            locale: 'date_range',
            type: 'dateRangePicker',
            active: filters.active.dateRange,
            locked: true,
            dateRanges: [THIS_MONTH, LAST_MONTH, THIS_QUARTER, LAST_QUARTER, THIS_YEAR, LAST_YEAR, CUSTOM_RANGE],
            values: {
                rangeType: filters.values.dateRange.rangeType,
                startDate: filters.values.dateRange.startDate,
                endDate: filters.values.dateRange.endDate
            },
            onChange: onChangeDateFilter('dateRange'),
            onToggle: onToggleFilter('dateRange')
        }
    ], [filters, onChangeDateFilter, onToggleFilter]);
    return {
        filters: filtersConfig,
        currentFilters: filters,
        onResetFilters,
        onResetCache,
    };
}
